import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { COMPONENTS_TEMPLATES, EPSILON_DPID, EPSILON_TENANT_ID } from '../data/component-templates-config';
import { AuthService } from './auth.service';
import { OverlayService } from './overlay.service';
import { Subject, Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, retry, shareReplay } from 'rxjs/operators';
import { NewApiHttpService, NewApiResponse } from './new-api-http.service';

@Injectable()
export class ComponentService {
	componentData: Subject<any> = new BehaviorSubject<any>(null);
	previewData: Subject<any> = new BehaviorSubject<any>(null);
	isPreviewPromiseCompleted: Subject<Boolean> = new BehaviorSubject<Boolean>(true);
	previewContent: Subject<any> = new BehaviorSubject<any>('<div></div>');
	timerPopupPosition: Subject<any> = new BehaviorSubject<any>({});

	disabledSave: Observable<any>;
	private _disabledSave: BehaviorSubject<any>;

	private eventSaveEmitSource: Subject<boolean> = new Subject<boolean>();
	eventSaveComponent$ = this.eventSaveEmitSource.asObservable();
	suportedComponentList$: Observable<any>;

	constructor(
		private http: HttpClient,
		private auth: AuthService,
		private overlay: OverlayService,
		private newApiHttpService: NewApiHttpService
	) {
		this._disabledSave = <BehaviorSubject<any>>new BehaviorSubject(false);
		this.disabledSave = this._disabledSave.asObservable();
		this.initSaveEvent();
		this.suportedComponentList$ = this.fetchSupportedComponents();
	}

	dispatchSaveEvent(): void {
		this.eventSaveEmitSource.next(true);
	}

	initSaveEvent(): void {
		this.eventSaveEmitSource.next(false);
	}

	private handleError(error: HttpErrorResponse) {
		if (error.error instanceof ErrorEvent) {
			// A client-side or network error occurred. Handle it accordingly.
			console.error('An error occurred:', error.error.message);
		} else if (typeof error === 'string') {
			console.error('An error occurred:' + error);
			return throwError('Something bad happened; please try again later. ' + error);
		} else {
			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong,
			console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
		}
		// return an ErrorObservable with a user-facing error message
		return throwError('Something bad happened; please try again later.');
	}

	apiRequest(method: string, data: any): Observable<CreatorFrontApi> {
		return this.http
			.post<CreatorFrontApi>(environment.apiURL + '/api/v2/?method=' + method, data)
			.pipe(retry(3), catchError(this.handleError));
	}

	updatePreviewData(): void {
		this.previewData.next(this.componentData);
	}

	updateDisabledSave(val): void {
		this._disabledSave.next(val);
	}

	fetchSupportedComponents(): Observable<NewApiResponse> {
		return this.newApiHttpService.newApiRequestGet('supported-list', {}, true).pipe(shareReplay());
	}

	getSupportedComponents(): Observable<any> {
		return this.suportedComponentList$;
	}

	fetchComponents(start, type, name, srv, order, direction) {
		const data = { start, type, name, srv, order, direction };
		return this.newApiHttpService.newApiRequestGet('widget', data);
	}

	updateComponentData(newData: Object): void {
		this.componentData.next(newData);
	}

	updatePreviewContent(html: string): void {
		this.previewContent.next(html);
	}

	updatePreviewPromiseStatus(promiseStatus: Boolean): void {
		this.isPreviewPromiseCompleted.next(promiseStatus);
	}

	getComponentTemplate(componentType, templateNum) {
		const isComponent = Boolean(
			COMPONENTS_TEMPLATES &&
				COMPONENTS_TEMPLATES.hasOwnProperty(componentType) &&
				COMPONENTS_TEMPLATES[componentType] &&
				COMPONENTS_TEMPLATES[componentType][templateNum]
		);

		if (isComponent) {
			const tenantId = this.auth.getUser().tenant_id;
			let componentTemplate = JSON.parse(JSON.stringify(COMPONENTS_TEMPLATES[componentType][templateNum]));
			if (tenantId === EPSILON_TENANT_ID && (componentTemplate.config.trackParams || componentTemplate.config.dm_params)) {
				if (componentTemplate.config.trackParams) {
					componentTemplate.config.trackParams.dpid = EPSILON_DPID;
				}
				if (componentTemplate.config.dm_params) {
					componentTemplate.config.dm_params.dpid = EPSILON_DPID;
				}
			}
			return componentTemplate;
		} else {
			return JSON.parse(
				JSON.stringify(
					COMPONENTS_TEMPLATES && COMPONENTS_TEMPLATES.hasOwnProperty(componentType) && COMPONENTS_TEMPLATES[componentType][0]
				)
			);
		}
	}

	getComponentData(componentId) {
		return this.newApiHttpService.newApiRequestGet(`widget/${componentId}`);
	}

	validateName(componentName, componentID) {
		const data = {
			name: componentName,
			id: componentID
		};
		return this.newApiHttpService.newApiRequestGet('widget/naming-validation', data);
	}

	checkCouponCounts(data) {
		return this.newApiHttpService.newApiRequestPost('widget/widget-data', data);
	}

	async clonename(name) {
		for (var i = 1; i < 1000; ) {
			var newName = name + ' (' + i + ')';
			let nameReturn = this.validateName(newName, -1).toPromise();
			var checkedResult = await nameReturn;
			if (checkedResult['data'] == true) {
				checkedResult['validName'] = newName;
				return checkedResult;
			} else {
				i++;
			}
		}
	}

	getComponentReport(componentId, fromDate, toDate, dpid) {
		this.overlay.updateOverlay(true, 'Generating Report...');

		const data = {};

		if (fromDate) {
			data['fromDate'] = fromDate;
		}

		if (toDate) {
			data['dateTo'] = toDate;
		}

		if (dpid) {
			data['dpid'] = dpid;
		}

		return this.newApiHttpService.newApiRequestGet(`report/component/${componentId}`, data);
	}

	getSurveyReport(componentId, fromDate, dateTo, dpid, user) {
		const data = {
			fromDate,
			dateTo,
			user
		};

		if (dpid) {
			data['dpid'] = dpid;
		}
		return this.newApiHttpService.newApiRequestGet(`report/survey/component/${componentId}`, data);
	}

	deleteComponent(componentId) {
		const sure = confirm('Are you sure you want to delete this component?');
		if (sure) {
			const data = {
				widgetId: componentId,
				action: 'delete'
			};

			return new Promise((resolve, reject) => {
				this.newApiHttpService.newApiRequestPut('widget', data).subscribe(
					(response) => {
						resolve(response);
					},
					(error) => {
						reject(error);
					}
				);
			});
		}
	}

	public getTimerPopupPosition(): void {
		const element = document.getElementById('countdown');
		if (element !== null) {
			const rect = element.getBoundingClientRect();
			this.timerPopupPosition.next({
				popUpTop: +rect.top,
				popUpLeft: +rect.left
			});
		}
	}
}
export interface CreatorFrontApi {
	status: boolean;
	data?: any;
	total?: any;
	error?: any;
	reason?: any;
}

export interface IFilterComponents {
	label: string;
	value: number;
}
